(() => {
  const UTM_STORAGE_KEY = 'interactor:utm-campaign';
  const GREETING_SHOWN_STORAGE_KEY = 'ridge-harbor:interactor-greeting-shown';
  const HAS_OPENED_STORAGE_KEY = 'interactor:has-opened';
  const INTERACTOR_FLAG = '__ridgeHarborCampaignTracking';
  const MESSAGE_DELAY = 3500;
  const MESSAGE_INTERVAL = 2200;

  let hideGreeting = () => {};

  const readSessionValue = (key) => {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  };

  const writeSessionValue = (key, value) => {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browsing modes.
    }
  };

  const readLocalValue = (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  };

  const writeLocalValue = (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browsing modes.
    }
  };

  const getInteractorUtmCampaign = () => {
    const storedCampaign = readSessionValue(UTM_STORAGE_KEY);

    if (storedCampaign) {
      try {
        return JSON.parse(storedCampaign);
      } catch (error) {
        // Replace malformed storage with valid campaign data when available.
      }
    }

    const searchParams = new URLSearchParams(window.location.search);
    const campaign = {
      source: searchParams.get('utm_source')?.trim(),
      medium: searchParams.get('utm_medium')?.trim(),
      campaign: searchParams.get('utm_campaign')?.trim(),
      content: searchParams.get('utm_content')?.trim(),
    };

    const populatedCampaign = Object.fromEntries(
      Object.entries(campaign).filter(([, value]) => Boolean(value))
    );

    if (Object.keys(populatedCampaign).length === 0) return undefined;

    writeSessionValue(UTM_STORAGE_KEY, JSON.stringify(populatedCampaign));
    return populatedCampaign;
  };

  const wrapInitialize = (interactor, initialize) => {
    if (typeof initialize !== 'function' || initialize[INTERACTOR_FLAG]) return initialize;

    const wrappedInitialize = (reference, options = {}) => {
      const originalOnOpen = options.onOpen;

      return initialize.call(interactor, reference, {
        ...options,
        utmCampaign: options.utmCampaign || getInteractorUtmCampaign(),
        onOpen: (layout) => {
          hideGreeting();
          if (typeof originalOnOpen === 'function') originalOnOpen(layout);
        },
      });
    };

    Object.defineProperty(wrappedInitialize, INTERACTOR_FLAG, {
      value: true,
      configurable: false,
    });

    return wrappedInitialize;
  };

  const prepareInteractor = (interactor) => {
    if (!interactor || interactor[INTERACTOR_FLAG]) return interactor;

    let currentInitialize = wrapInitialize(interactor, interactor.initialize);

    try {
      Object.defineProperty(interactor, 'initialize', {
        configurable: true,
        get() {
          return currentInitialize;
        },
        set(nextInitialize) {
          currentInitialize = wrapInitialize(interactor, nextInitialize);
        },
      });

      Object.defineProperty(interactor, INTERACTOR_FLAG, {
        value: true,
        configurable: false,
      });
    } catch (error) {
      interactor.initialize = wrapInitialize(interactor, interactor.initialize);
    }

    return interactor;
  };

  let currentInteractor = prepareInteractor(window.interactor);

  try {
    Object.defineProperty(window, 'interactor', {
      configurable: true,
      get() {
        return currentInteractor;
      },
      set(nextInteractor) {
        currentInteractor = prepareInteractor(nextInteractor);
      },
    });
  } catch (error) {
    prepareInteractor(window.interactor);
  }

  const playGreetingChime = () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;

      const audioContext = new AudioContext();
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      const now = audioContext.currentTime;

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(720, now);
      oscillator.frequency.exponentialRampToValueAtTime(900, now + 0.08);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.025, now + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);

      oscillator.connect(gain);
      gain.connect(audioContext.destination);
      oscillator.start(now);
      oscillator.stop(now + 0.12);
      oscillator.addEventListener('ended', () => {
        audioContext.close();
      }, { once: true });
    } catch (error) {
      // Sound is a progressive enhancement and may be blocked by the browser.
    }
  };

  const initializeGreeting = () => {
    const greeting = document.querySelector('[data-interactor-greeting]');
    if (!greeting) return;

    const messages = Array.from(
      greeting.querySelectorAll('[data-interactor-greeting-message]')
    );
    const openTriggers = greeting.querySelectorAll('[data-interactor-greeting-open]');
    const dismissTrigger = greeting.querySelector('[data-interactor-greeting-dismiss]');
    const timers = [];
    let greetingEnabled = true;
    let pageHasInteraction = false;

    const recordInteraction = () => {
      pageHasInteraction = true;
    };

    const markGreetingHandled = () => {
      writeSessionValue(GREETING_SHOWN_STORAGE_KEY, 'true');
    };

    hideGreeting = ({ remember = false } = {}) => {
      greetingEnabled = false;
      greeting.hidden = true;
      messages.forEach((message) => {
        message.hidden = true;
      });
      timers.forEach((timer) => window.clearTimeout(timer));
      if (remember) markGreetingHandled();
    };

    const openInteractor = () => {
      hideGreeting({ remember: true });
      writeLocalValue(HAS_OPENED_STORAGE_KEY, 'true');

      if (window.interactor?.modal?.open) {
        window.interactor.modal.open();
        return;
      }

      document.getElementById('interactor-fab')?.click();
    };

    openTriggers.forEach((trigger) => {
      trigger.addEventListener('click', openInteractor);
    });

    dismissTrigger?.addEventListener('click', () => {
      hideGreeting({ remember: true });
    });

    window.addEventListener('pointerdown', recordInteraction, { once: true });
    window.addEventListener('keydown', recordInteraction, { once: true });

    const hasOpenedInteractor = readLocalValue(HAS_OPENED_STORAGE_KEY) === 'true';
    const hasShownGreeting = readSessionValue(GREETING_SHOWN_STORAGE_KEY) === 'true';
    if (hasOpenedInteractor || hasShownGreeting) return;

    messages.forEach((message, index) => {
      const timer = window.setTimeout(() => {
        if (!greetingEnabled) return;

        greeting.hidden = false;
        message.hidden = false;

        if (pageHasInteraction) playGreetingChime();

        if (index === messages.length - 1) markGreetingHandled();
      }, MESSAGE_DELAY + index * MESSAGE_INTERVAL);

      timers.push(timer);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeGreeting, { once: true });
  } else {
    initializeGreeting();
  }
})();
